import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import API from '../../utils/api';
import { createPeer, getMedia, startRing } from '../../utils/videoCall';
import CallWindow from './CallWindow';
import './VideoCall.css';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER = apiBase.replace(/\/api\/?$/, '');

/** Start a call from anywhere in the admin panel:
 *  window.dispatchEvent(new CustomEvent('admin-start-call', { detail: { userId, name } })) */
export const startAdminCall = (userId, name) => window.dispatchEvent(new CustomEvent('admin-start-call', { detail: { userId, name } }));

/**
 * The admin's side of a video call: calls a member or service provider, shows "Ringing…" and, when they accept,
 * the call. The person gets a ringing pop-up on the website or a notification + ring in the mobile app.
 */
const AdminCallHost = () => {
    const [call, setCall] = useState(null); // { callId, title, status, note, localStream, remoteStream }
    const socketRef = useRef(null);
    const peerRef = useRef(null);
    const callRef = useRef(null);
    const stopRingRef = useRef(null);
    callRef.current = call;

    const cleanup = useCallback(() => {
        stopRingRef.current?.(); stopRingRef.current = null;
        peerRef.current?.close(); peerRef.current = null;
        callRef.current?.localStream?.getTracks().forEach(track => track.stop());
        setCall(null);
    }, []);

    // one socket for calls, logged in as the admin (the server checks the token)
    useEffect(() => {
        const socket = io(SERVER, { auth: (cb) => cb({ token: localStorage.getItem('adminToken') }), transports: ['websocket', 'polling'] });
        socketRef.current = socket;
        socket.on('call:accepted', async (data) => {
            if (callRef.current?.callId !== data.callId) return;
            stopRingRef.current?.(); stopRingRef.current = null;
            setCall(c => (c ? { ...c, status: 'connecting', note: '' } : c));
            try { await peerRef.current?.start(); } catch { toast.error('Could not connect the call'); cleanup(); }
        });
        socket.on('call:signal', (data) => { if (peerRef.current && callRef.current?.callId === data.callId) peerRef.current.apply(data.data).catch(() => { }); });
        const over = (message) => (data) => {
            if (callRef.current?.callId !== data.callId) return;
            if (message) toast(message);
            cleanup();
        };
        socket.on('call:declined', over('The person declined the call.'));
        socket.on('call:missed', over('No answer. The call timed out.'));
        socket.on('call:ended', over('The call ended.'));
        return () => { socket.disconnect(); socketRef.current = null; cleanup(); };
    }, [cleanup]);

    useEffect(() => {
        const onStart = async (event) => {
            const { userId, name } = event.detail || {};
            if (!userId || callRef.current) { if (callRef.current) toast('You are already on a call.'); return; }
            try {
                const localStream = await getMedia();
                const ice = await API.get('/calls/ice');
                const socket = socketRef.current;
                if (!socket?.connected) { localStream.getTracks().forEach(t => t.stop()); toast.error('Not connected to the server. Try again in a moment.'); return; }
                socket.emit('call:invite', { targetUserId: userId }, (reply) => {
                    if (!reply?.ok) { localStream.getTracks().forEach(t => t.stop()); toast.error(reply?.message || 'Could not start the call'); return; }
                    const peer = createPeer({
                        socket, callId: reply.callId, iceServers: ice.data.iceServers, localStream, isCaller: true,
                        onRemoteStream: (remoteStream) => setCall(c => (c ? { ...c, remoteStream } : c)),
                        onState: (state) => {
                            if (state === 'connected') setCall(c => (c ? { ...c, status: 'connected' } : c));
                            if (state === 'failed') toast.error('The video could not connect. This network blocked a direct connection, so a TURN relay server is needed.');
                            if (['failed', 'closed'].includes(state)) cleanup();
                        },
                    });
                    peerRef.current = peer;
                    stopRingRef.current = startRing();
                    setCall({ callId: reply.callId, targetUserId: userId, title: name || 'Member', status: 'ringing', note: reply.online ? '' : 'Not online now. A notification was sent to their phone.', localStream, remoteStream: null });
                });
            } catch (error) {
                toast.error(error.message || 'Could not start the call');
            }
        };
        window.addEventListener('admin-start-call', onStart);
        return () => window.removeEventListener('admin-start-call', onStart);
    }, [cleanup]);

    // saves a screenshot / recording in the person's own folder on the server (the fields go before the file)
    const saveMedia = async ({ kind, blob, fileName, durationSec }) => {
        const active = call; // this render's call: still right when the recording ends because the call ended
        if (!active) throw new Error('no call');
        const form = new FormData();
        form.append('targetUserId', active.targetUserId);
        form.append('callId', active.callId);
        form.append('kind', kind);
        form.append('fileName', fileName);
        form.append('durationSec', String(Math.round(durationSec || 0)));
        form.append('file', blob, fileName);
        await API.post('/calls/media', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0, maxBodyLength: Infinity });
        toast.success(kind === 'recording' ? 'Recording saved in Call recordings' : 'Screenshot saved in Call recordings');
    };

    const hangup = () => {
        if (call) socketRef.current?.emit(call.status === 'ringing' ? 'call:cancel' : 'call:end', { callId: call.callId });
        cleanup();
    };

    return call ? <CallWindow title={call.title} status={call.status} note={call.note} remoteStream={call.remoteStream} localStream={call.localStream} onHangup={hangup} onRecordingChange={(on) => socketRef.current?.emit('call:recording', { callId: call.callId, on })} onSaveMedia={saveMedia} /> : null;
};

export default AdminCallHost;
